import { z } from 'zod';

// User Schema
export const UserDTOSchema = z.object({
  id: z.number(),
  username: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
});

export type UserDTOType = z.infer<typeof UserDTOSchema>;

// Prompt Template Schema
export const PromptTemplateDTOSchema = z.object({
  id: z.number(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  content: z.string(),
  template_type: z.enum([
    'standard_workflow_prompt',
    'occasion_workflow_prompt',
    'image_generation_prompt',
  ]),
  is_active: z.boolean(),
  model_config: z.record(z.string(), z.unknown()).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  created_on: z.string(),
  updated_on: z.string(),
});

export type PromptTemplateDTOType = z.infer<typeof PromptTemplateDTOSchema>;

export const PromptTemplateListDTOSchema = z.object({
  id: z.number(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  template_type: z.enum([
    'standard_workflow_prompt',
    'occasion_workflow_prompt',
    'image_generation_prompt',
  ]),
  is_active: z.boolean(),
  updated_on: z.string(),
  content: z.string(),
});

export type PromptTemplateListDTOType = z.infer<typeof PromptTemplateListDTOSchema>;

// Styling Workflow Models (Updated to match new backend structure)
export const StylingWorkflowDTOSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  prompt_template: PromptTemplateListDTOSchema.nullable().optional(),
  image_prompt_template: z.number().nullable().optional(),
  workflow_genre: z.enum(['standard', 'occasion']).optional(),
  occasion_id: z.number().nullable().optional(),
  sub_occasion_id: z.number().nullable().optional(),
  occasion_name: z.string().nullable().optional(),
  sub_occasion_name: z.string().nullable().optional(),
  instructions: z.string().nullable().optional(),
  look_variants_required: z.number().optional(),
  status: z.enum(['draft', 'running', 'paused', 'stopped', 'completed', 'failed']),
  total_items: z.number(),
  completed_items: z.number(),
  failed_items: z.number(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  estimated_completion_at: z.string().nullable(),
  progress_percentage: z.number(),
  current_item_description: z.string(),
  error_message: z.string(),
  created_on: z.string(),
  updated_on: z.string(),
  looks: z.array(z.lazy(() => LookDTOSchema)).optional(),
  looks_count: z.number().optional(),
});

export type StylingWorkflowDTOType = z.infer<typeof StylingWorkflowDTOSchema>;

export const StylingWorkflowCreateDTOSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  prompt_template: z.number().optional(),
  image_prompt_template: z.number().nullable().optional(),
  workflow_genre: z.enum(['standard', 'occasion']).optional(),
  occasion_id: z.number().nullable().optional(),
  sub_occasion_id: z.number().nullable().optional(),
  instructions: z.string().optional(),
  look_variants_required: z.number().optional(),
});

export type StylingWorkflowCreateDTOType = z.infer<typeof StylingWorkflowCreateDTOSchema>;

export const StylingWorkflowRunItemDTOSchema = z.object({
  base_product_id: z.string(),
  lk_style_number: z.string().optional(),
  base_product_type: z.string(),
  source_image_url: z.string(),
  base_product_color: z.string(),
  base_product_handle: z.string().optional(), // Shopify handle for the product
  base_product_variant_id: z.string().optional(),
  base_product_brand_name: z.string().optional(),
  base_product_name: z.string().optional(),
  image_generation_model: z.string().optional(),
  category: z.string().optional(),
  sub_category: z.string().optional(),
  merch_type: z.string().optional(),
  pack_contains: z.string().optional(),
  fabric_name: z.string().optional(),
  fabric_description: z.string().optional(),
});

export type StylingWorkflowRunItemDTOType = z.infer<typeof StylingWorkflowRunItemDTOSchema>;

export const StylingWorkflowDraftDTOSchema = StylingWorkflowCreateDTOSchema.extend({
  image_generation_model: z.string().optional(),
  items: z.array(StylingWorkflowRunItemDTOSchema).optional(),
});

export type StylingWorkflowDraftDTOType = z.infer<typeof StylingWorkflowDraftDTOSchema>;

export const ImageModelDTOSchema = z.object({
  id: z.string(),
  litellm_model: z.string(),
  display_name: z.string(),
  description: z.string(),
  supported_methods: z.array(z.string()).optional(),
  version: z.string().optional(),
  provider: z.enum(['gemini', 'openai']).optional(),
});

export type ImageModelDTOType = z.infer<typeof ImageModelDTOSchema>;

export const ImageModelListResponseSchema = z.object({
  default: z.string(),
  models: z.array(ImageModelDTOSchema),
});

export type ImageModelListResponseType = z.infer<typeof ImageModelListResponseSchema>;

export const OccasionImageAssetSchema = z.object({
  url: z.string().optional(),
  id: z.number().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
  usage: z.string().optional(),
  uploaded: z.boolean().optional(),
});

export type OccasionImageAssetType = z.infer<typeof OccasionImageAssetSchema>;

export const OccasionDTOSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: OccasionImageAssetSchema.nullable().optional(),
  icon_light: OccasionImageAssetSchema.nullable().optional(),
  icon_dark: OccasionImageAssetSchema.nullable().optional(),
  parent_id: z.number().nullable().optional(),
  display_order: z.number(),
  is_active: z.boolean().optional(),
});

export type OccasionDTOType = z.infer<typeof OccasionDTOSchema>;

export const OccasionCreateRequestSchema = z.object({
  name: z.string(),
  parent_id: z.number().nullable().optional(),
  display_order: z.number().optional(),
  is_active: z.boolean().optional(),
  image: OccasionImageAssetSchema.nullable().optional(),
  icon_light: OccasionImageAssetSchema.nullable().optional(),
  icon_dark: OccasionImageAssetSchema.nullable().optional(),
});

export type OccasionCreateRequestType = z.infer<typeof OccasionCreateRequestSchema>;

export const OccasionUpdateRequestSchema = OccasionCreateRequestSchema.partial().extend({
  id: z.number(),
});

export type OccasionUpdateRequestType = z.infer<typeof OccasionUpdateRequestSchema>;

export type OccasionTreeItemType = {
  id: number;
  name: string;
  image?: OccasionImageAssetType | null;
  icon_light?: OccasionImageAssetType | null;
  icon_dark?: OccasionImageAssetType | null;
  parent_id?: number | null;
  display_order: number;
  is_active?: boolean;
  children: Array<OccasionTreeItemType>;
};

export const OccasionTreeItemSchema: z.ZodType<OccasionTreeItemType> = z.lazy(() =>
  z.object({
    id: z.number(),
    name: z.string(),
    image: OccasionImageAssetSchema.nullable().optional(),
    icon_light: OccasionImageAssetSchema.nullable().optional(),
    icon_dark: OccasionImageAssetSchema.nullable().optional(),
    parent_id: z.number().nullable().optional(),
    display_order: z.number(),
    is_active: z.boolean().optional(),
    children: z.array(OccasionTreeItemSchema).default([]),
  }),
);

// Look Model (represents individual styling looks)
export const LookDTOSchema = z.object({
  id: z.number(),
  workflow: z.number().optional().nullable(),
  workflow_genre: z.enum(['standard', 'occasion']).optional(),
  group_display_order: z.number().optional(),
  is_active: z.boolean().nullable(),
  base_product_id: z.string(),
  lk_style_number: z.string().optional().nullable(),
  base_product_variant_id: z.string().optional().nullable(),
  base_product_type: z.string(),
  base_product_brand_name: z.string().optional().default(''),
  base_product_name: z.string().optional().default(''),
  base_product_handle: z.string().optional().default(''),
  base_product_color: z.string().optional().default(''),
  source_image_url: z.string(),
  occasion_name: z.string(),
  sub_occasion_name: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  scene_description: z.string().optional().nullable(),
  layering_type: z.enum(['with_layering', 'without_layering']),
  blurb: z.string(),
  product_recommendations: z.array(
    z.object({
      product_type: z.string(),
      description: z.string(),
      mapped_lk_styles: z.array(z.string()).optional(),
      mapped_product_images: z.record(z.string(), z.any()).optional(),
    }),
  ),
  search_seeds: z
    .array(
      z.object({
        product_type: z.string(),
        description: z.string().default(''),
        queries: z.array(z.string()).default([]),
        search_seed: z.string().default(''),
        product_type_filters: z.array(z.string()).default([]),
        color_filters: z.array(z.string()).default([]),
        mapped_lk_styles: z.array(z.string()).default([]),
        mapped_product_images: z.record(z.string(), z.string()).default({}),
      }),
    )
    .optional()
    .default([]),
  visual_search: z
    .array(
      z.object({
        product_type: z.string().optional(),
        item_key: z.string().optional(),
        product_label: z.string().optional(),
        product_description: z.string().optional(),
        product_type_filters: z.array(z.string()).optional(),
        color_filters: z.array(z.string()).optional(),
        search_seed: z.string().optional(),
        isolation_notes: z.string().optional(),
        isolated_image_url: z.string().optional(),
        mapped_lk_styles: z.array(z.string()).optional(),
        mapped_product_images: z.record(z.string(), z.string()).optional(),
      }),
    )
    .optional()
    .default([]),
  widgets: z
    .object({
      status: z.string().optional(),
      version: z.number().optional(),
      items: z.array(z.record(z.string(), z.any())).optional().default([]),
      built_at: z.string().nullable().optional(),
      input_hash: z.string().optional(),
      build_error: z.string().optional(),
    })
    .optional(),
  look_name: z.string().optional(),
  look_hook: z.string().optional(),
  image_prompt: z.string().optional(),
  image_generation_variables: z.record(z.string(), z.any()).optional(),
  generated_image_url: z.string(),
  image_generation_status: z.enum(['pending', 'generating', 'completed', 'failed']),
  image_generation_model: z.string().optional().nullable(),
  qc_status: z.enum(['pending', 'published', 'rejected']),
  qc_feedback: z.string(),
  qc_at: z.string().nullable(),
  published_at: z.string().nullable(),
  content_edited: z.boolean(),
  content_edited_at: z.string().nullable(),
  generation_error: z.string(),
  retry_count: z.number(),
  metadata: z.record(z.string(), z.any()).optional(),
  created_on: z.string(),
  updated_on: z.string(),
});

export type LookDTOType = z.infer<typeof LookDTOSchema>;

export const LookGroupTaggedProductDTOSchema = z.object({
  id: z.string(),
  name: z.string().optional().default(''),
  asset_url: z.string().optional().default(''),
  deep_link: z.string().optional().default(''),
  lk_style_number: z.string().optional().default(''),
});

export type LookGroupTaggedProductDTOType = z.infer<typeof LookGroupTaggedProductDTOSchema>;

export const LookGroupLookDTOSchema = z.object({
  id: z.number(),
  group_display_order: z.number().optional(),
  is_active: z.boolean().nullable(),
  lk_style_number: z.string().optional().nullable(),
  qc_status: z.string().optional(),
  image_generation_status: z.string().optional(),
  look_name: z.string().optional(),
  look_hook: z.string().optional(),
  image: z.string().optional(),
  created_on: z.string().optional(),
  generated_image_url: z.string().optional(),
  source_image_url: z.string().optional(),
  tagged_product_count: z.number().optional().default(0),
  tagged_products: z
    .array(LookGroupTaggedProductDTOSchema)
    .nullish()
    .transform(value => value ?? []),
});

export type LookGroupLookDTOType = z.infer<typeof LookGroupLookDTOSchema>;

export const LookGroupQCSummaryDTOSchema = z.object({
  total_looks: z.number(),
  tagged_looks: z.number(),
  needs_action: z.number(),
  published_count: z.number(),
  pending_count: z.number(),
  rejected_count: z.number(),
});

export type LookGroupQCSummaryDTOType = z.infer<typeof LookGroupQCSummaryDTOSchema>;

export const LookGroupDTOSchema = z.object({
  id: z.number(),
  occasion_name: z.string(),
  sub_occasion_name: z.string().optional().default(''),
  base_product_id: z.string(),
  base_product_type: z.string().optional().default(''),
  base_product_brand_name: z.string().optional().default(''),
  base_product_name: z.string().optional().default(''),
  base_product_color: z.string().optional().default(''),
  is_pinned: z.boolean(),
  display_order: z.number(),
  look_count: z.number().optional(),
  primary_look_id: z.number().nullable().optional(),
  primary_look: LookGroupLookDTOSchema.nullable().optional(),
  looks: z
    .array(LookGroupLookDTOSchema)
    .nullish()
    .transform(value => value ?? []),
  qc_summary: LookGroupQCSummaryDTOSchema.nullish(),
  created_on: z.string().optional(),
  updated_on: z.string().optional(),
});

export type LookGroupDTOType = z.infer<typeof LookGroupDTOSchema>;

export const LookGroupListResponseSchema = z.array(LookGroupDTOSchema);

export type LookGroupListResponseType = z.infer<typeof LookGroupListResponseSchema>;

// Workflow Control Schema
export const StylingWorkflowControlDTOSchema = z.object({
  id: z.number(),
  workflow: z.number(),
  action: z.enum(['pause', 'stop', 'resume']),
  requested_by: UserDTOSchema.nullable(),
  requested_at: z.string(),
  applied_at: z.string().nullable(),
});

export type StylingWorkflowControlDTOType = z.infer<typeof StylingWorkflowControlDTOSchema>;
// QC Models
export const QCApprovalDTOSchema = z.object({
  look_id: z.number(),
  feedback: z.string().optional(),
  quality_score: z.number().optional(),
});

export type QCApprovalDTOType = z.infer<typeof QCApprovalDTOSchema>;

export const QCRejectionDTOSchema = z.object({
  look_id: z.number(),
  feedback: z.string(),
  rejection_reason: z.enum([
    'poor_quality',
    'incorrect_styling',
    'technical_issues',
    'brand_guidelines',
    'other',
  ]),
});

export type QCRejectionDTOType = z.infer<typeof QCRejectionDTOSchema>;

// Publishing Models
export const PublishingDTOSchema = z.object({
  look_ids: z.array(z.number()),
  publish_immediately: z.boolean().optional(),
  scheduled_publish_at: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type PublishingDTOType = z.infer<typeof PublishingDTOSchema>;

// Additional API Types
export const CostEstimateRequestSchema = z.object({
  items_count: z.number(),
});

export type CostEstimateRequestType = z.infer<typeof CostEstimateRequestSchema>;

export const CostEstimateResponseSchema = z.object({
  items_count: z.number(),
  estimated_text_tokens: z.number(),
  estimated_image_generations: z.number(),
  estimated_cost_usd: z.string(),
  breakdown: z.record(z.string(), z.unknown()),
});

export type CostEstimateResponseType = z.infer<typeof CostEstimateResponseSchema>;

export const WorkflowProgressResponseSchema = z.object({
  workflow_id: z.number(),
  status: z.enum(['draft', 'running', 'paused', 'stopped', 'completed', 'failed']),
  progress_percentage: z.number(),
  total_items: z.number(),
  completed_items: z.number(),
  failed_items: z.number(),
  estimated_completion_at: z.string().nullable(),
  current_item_description: z.string(),
  actual_cost: z.string(),
  total_tokens_used: z.number(),
});

export type WorkflowProgressResponseType = z.infer<typeof WorkflowProgressResponseSchema>;

export const QCReviewRequestSchema = z.object({
  action: z.enum(['approve', 'reject']),
  feedback: z.string().optional(),
  quality_score: z.number().optional(),
  rejection_reason: z
    .enum(['poor_quality', 'incorrect_styling', 'technical_issues', 'brand_guidelines', 'other'])
    .optional(),
});

export type QCReviewRequestType = z.infer<typeof QCReviewRequestSchema>;

export const ContentUpdateRequestSchema = z.object({
  occasion_name: z.string().optional(),
  sub_occasion_name: z.string().optional(),
  description: z.string().optional(),
  scene_description: z.string().optional(),
  layering_type: z.enum(['with_layering', 'without_layering']).optional(),
  blurb: z.string().optional(),
  look_name: z.string().optional(),
  look_hook: z.string().optional(),
  is_active: z.boolean().optional(),
  website_content: z.record(z.string(), z.any()).optional(),
  search_seeds: z
    .array(
      z.object({
        product_type: z.string(),
        description: z.string().default(''),
        queries: z.array(z.string()).default([]),
        search_seed: z.string().default(''),
        product_type_filters: z.array(z.string()).default([]),
        color_filters: z.array(z.string()).default([]),
        mapped_lk_styles: z.array(z.string()).default([]),
        mapped_product_images: z.record(z.string(), z.string()).default({}),
      }),
    )
    .optional(),
  visual_search: z
    .array(
      z.object({
        product_type: z.string().optional(),
        item_key: z.string().optional(),
        product_label: z.string().optional(),
        product_description: z.string().optional(),
        product_type_filters: z.array(z.string()).optional(),
        color_filters: z.array(z.string()).optional(),
        search_seed: z.string().optional(),
        isolation_notes: z.string().optional(),
        isolated_image_url: z.string().optional(),
        mapped_lk_styles: z.array(z.string()).optional(),
        mapped_product_images: z.record(z.string(), z.string()).optional(),
      }),
    )
    .optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  product_recommendations: z
    .array(
      z.object({
        product_type: z.string(),
        description: z.string(),
        mapped_lk_styles: z.array(z.string()).optional(),
        mapped_product_images: z.record(z.string(), z.any()).optional(),
      }),
    )
    .optional(),
});

export type ContentUpdateRequestType = z.infer<typeof ContentUpdateRequestSchema>;

// Publish Request Schema
export const PublishRequestSchema = z.object({
  qc_status: z.enum(['published', 'rejected']),
  qc_feedback: z.string().optional(),
});

export type PublishRequestType = z.infer<typeof PublishRequestSchema>;

// List Response Schemas
export const StylingWorkflowListItemDTOSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  prompt_template_name: z.string().optional().nullable(),
  image_prompt_template_name: z.string().optional().nullable(),
  workflow_genre: z.enum(['standard', 'occasion']).optional(),
  occasion_id: z.number().optional().nullable(),
  sub_occasion_id: z.number().optional().nullable(),
  instructions: z.string().optional().nullable(),
  look_variants_required: z.number().optional(),
  status: z.enum(['draft', 'running', 'paused', 'stopped', 'completed', 'failed']),
  total_items: z.number(),
  completed_items: z.number(),
  failed_items: z.number(),
  progress_percentage: z.number(),
  created_on: z.string(),
  looks_count: z.number(),
  error_message: z.string().optional(),
});

export type StylingWorkflowListItemDTOType = z.infer<typeof StylingWorkflowListItemDTOSchema>;

export const StylingWorkflowListResponseSchema = z.object({
  results: z.array(StylingWorkflowListItemDTOSchema),
  facets: z
    .array(
      z.object({
        key: z.object({ key: z.string(), displayName: z.string() }),
        values: z.array(
          z.object({
            id: z.string(),
            value: z.string(),
            count: z.number().optional(),
          }),
        ),
      }),
    )
    .optional(),
});

export type StylingWorkflowListResponseType = z.infer<typeof StylingWorkflowListResponseSchema>;

export const OccasionListResponseSchema = z.array(OccasionTreeItemSchema);

export type OccasionListResponseType = z.infer<typeof OccasionListResponseSchema>;

export const LookListResponseSchema = z.object({
  results: z.array(LookDTOSchema),
  facets: z
    .array(
      z.object({
        key: z.object({ key: z.string(), displayName: z.string() }),
        values: z.array(
          z.object({
            id: z.string(),
            value: z.string(),
            count: z.number().optional(),
          }),
        ),
      }),
    )
    .optional(),
});

export type LookListResponseType = z.infer<typeof LookListResponseSchema>;

// For backward compatibility with existing code, we create aliases
export const StylingWorkflowRunDTOSchema = LookDTOSchema;
export type StylingWorkflowRunDTOType = LookDTOType;

export const StylingWorkflowRunListResponseSchema = LookListResponseSchema;
export type StylingWorkflowRunListResponseType = LookListResponseType;

// Legacy aliases (to prevent breaking changes)
export const StylingWorkflowSchema = StylingWorkflowDTOSchema;
export const LookSchema = LookDTOSchema;

export type StylingWorkflowType = StylingWorkflowDTOType;
export type LookType = LookDTOType;
